import React, { useState, useEffect } from "react";
import { Formik, Field, FieldArray, FormikProps } from "formik";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import "../../../App.css";
import color from "../../../components/utils/Colors";
import { useNavigate } from "react-router-dom";
import { questionScheema } from "../../../components/schema/schema";
import { ArrowDropDown } from "@mui/icons-material";
import { QnsTypeSortBy } from "../../../components/utils/Data";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import { jwtDecode } from "jwt-decode";
import { getDataFromTable } from "../../../services/services";

const QuestionForm = () => {
  const [isEditable] = useState(true);
  const [questionType, setQuestionType] = useState("MCQ");

  // Course states
  const [courses, setCourses] = useState<
    { id: string | number; title: string }[]
  >([]);
  const [courseId, setCourseId] = useState<string | number>("");
  const [coursesLoading, setCoursesLoading] = useState(false);

  // Lecture states
  const [lectures, setLectures] = useState<
    { id: string | number; lectureName: string; courseId: string | number }[]
  >([]);
  const [lectureId, setLectureId] = useState<string | number>("");
  const [lectureName, setLectureName] = useState("");
  const [lecturesLoading, setLecturesLoading] = useState(false);

  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const decoded: any = jwtDecode(token);
        return decoded.id || decoded.userId || decoded.sub;
      }
    } catch (err) {
      console.error("Token decode error:", err);
    }
    return null;
  };

  // Fetch teacher's courses on mount
  useEffect(() => {
    const fetchCourses = async () => {
      setCoursesLoading(true);
      try {
        const token = localStorage.getItem("accessToken");
        let currentUserId: string | number = "";
        if (token) {
          const decoded: any = jwtDecode(token);
          currentUserId = decoded.id || decoded.userId || decoded.sub;
        }
        const res = await getDataFromTable("course");
        const allItems = res?.data?.data || res?.data || res || [];
        const filtered = allItems.filter(
          (item: any) => String(item.createdBy) === String(currentUserId),
        );
        setCourses(filtered);
      } catch (err) {
        console.error("Courses fetch error:", err);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Fetch lectures when courseId changes
  useEffect(() => {
    if (!courseId) {
      setLectures([]);
      setLectureId("");
      setLectureName("");
      return;
    }
    const fetchLectures = async () => {
      setLecturesLoading(true);
      try {
        const token = localStorage.getItem("accessToken");
        let currentUserId: string | number = "";
        if (token) {
          const decoded: any = jwtDecode(token);
          currentUserId = decoded.id || decoded.userId || decoded.sub;
        }
        const res = await getDataFromTable("courseContent", { courseId });
        const allItems = res?.data?.data || res || [];
        const filtered = allItems.filter(
          (item: any) => String(item.createdBy) === String(currentUserId),
        );
        console.log(allItems);
        setLectures(filtered);
      } catch (err) {
        console.error("Lectures fetch error:", err);
      } finally {
        setLecturesLoading(false);
      }
    };
    fetchLectures();
  }, [courseId]);

  const handlePreview = async (
    formik: FormikProps<{
      questions: {
        question: string;
        options: string[];
        correctOption: string;
        correctAnswer: string;
      }[];
    }>,
  ) => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
      navigate("/question-form-preview", {
        state: {
          questions: formik.values.questions,
          questionType,
          lectureId,
          lectureName,
          courseId,
        },
      });
    } else {
      formik.setTouched(
        formik.values.questions.reduce((acc: any, _: any, i: number) => {
          acc[i] = { question: true, correctOption: true, correctAnswer: true };
          return acc;
        }, {}),
      );
      alert("Please fill all required fields.");
    }
  };

  const handleSubmitForm = (values: any) => {
    console.log("All Questions :", JSON.stringify(values.questions, null, 2));
  };

  const inputLabelStyle = {
    marginBottom: "8px",
    ml: 0.5,
    color: color.textColor,
    fontWeight: "bold",
  };

  const handleGoBack = () => navigate(-1);

  return (
    <Box
      sx={{
        marginTop: isSmallScreen ? "25px" : "30px",
        flex: 1,
        borderRadius: "16px",
        paddingBottom: "5%",
        p: 2,
      }}
    >
      <Typography
        onClick={handleGoBack}
        sx={{
          margin: 0,
          position: "absolute",
          top: isSmallScreen ? "120px" : "140px",
          color: color.firstColor,
          fontSize: "16px",
          fontWeight: "bold",
          left: isSmallScreen ? 10 : 60,
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <ChevronLeft /> Back
      </Typography>

      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          color: color.textColor,
          width: "fit-content",
          margin: "auto",
          padding: "2px 10px",
          borderRadius: "4px",
          mb: 6,
        }}
      >
        Create your Questions
      </Typography>

      <Box
        sx={{
          background: color.secondColor,
          p: 2,
          pt: 4,
          borderRadius: 4,
          boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.09)",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          mb={4}
          px={2}
          sx={{ flexDirection: isSmallScreen ? "column" : "row", gap: 2 }}
        >
          {/* ── Select Course ── */}
          <FormControl
            className="input-root2"
            sx={{ width: isSmallScreen ? "70%" : "22%", borderRadius: "70px" }}
          >
            <Typography sx={inputLabelStyle}>Select Course</Typography>
            <Select
              style={{
                background: color.firstColor,
                color: "white",
                borderRadius: "80px",
                height: "30px",
              }}
              value={courseId}
              onChange={(e) => {
                setCourseId(e.target.value);
                setLectureId("");
                setLectureName("");
              }}
              displayEmpty
              IconComponent={(props) => (
                <ArrowDropDown {...props} style={{ color: "white" }} />
              )}
            >
              <MenuItem value="" disabled>
                {coursesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    Loading...
                  </Box>
                ) : (
                  "Select Course"
                )}
              </MenuItem>
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── Select Lecture ── */}
          <FormControl
            className="input-root2"
            sx={{ width: isSmallScreen ? "70%" : "22%", borderRadius: "70px" }}
          >
            <Typography sx={inputLabelStyle}>Select Lecture</Typography>
            <Select
              style={{
                background: courseId ? color.firstColor : "#aaa",
                color: "white",
                borderRadius: "80px",
                height: "30px",
              }}
              value={lectureId}
              onChange={(e) => {
                const selectedId = e.target.value;
                setLectureId(selectedId);
                const found = lectures.find(
                  (l) => String(l.id) === String(selectedId),
                );
                setLectureName(found?.lectureName || "");
              }}
              displayEmpty
              disabled={!courseId}
              IconComponent={(props) => (
                <ArrowDropDown {...props} style={{ color: "white" }} />
              )}
            >
              <MenuItem value="" disabled>
                {lecturesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    Loading...
                  </Box>
                ) : !courseId ? (
                  "Select course first"
                ) : (
                  "Select Lecture"
                )}
              </MenuItem>
              {lectures.map((lecture) => (
                <MenuItem key={lecture.id} value={lecture.id}>
                  {lecture.lectureName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* ── Select Question Type ── */}
          <FormControl
            className="input-root2"
            sx={{ width: isSmallScreen ? "70%" : "22%" }}
          >
            <Typography sx={inputLabelStyle}>Select Question Type</Typography>
            <Select
              style={{
                background: color.firstColor,
                color: "white",
                borderRadius: "80px",
                height: "20px",
              }}
              value={questionType}
              onChange={(e) => {
                setQuestionType(e.target.value);
              }}
              IconComponent={(props) => (
                <ArrowDropDown {...props} style={{ color: "white" }} />
              )}
            >
              {QnsTypeSortBy.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Formik
          initialValues={{
            questions: [
              {
                question: "",
                options: ["", "", "", ""],
                correctOption: "",
                correctAnswer: "",
              },
            ],
          }}
          validationSchema={questionScheema}
          onSubmit={handleSubmitForm}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <FieldArray name="questions">
                {({ remove, push }) => (
                  <div>
                    {/* ── SHORT QUESTIONS ── */}
                    {questionType === "Short Questions" &&
                      formik.values.questions.map((question, index) => (
                        <Box
                          key={index}
                          sx={{
                            borderRadius: "8px",
                            padding: "16px",
                            paddingBottom: 0,
                            position: "relative",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              mb: 2,
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: color.textColor,
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {`Question : ${index + 1}`}
                            </Typography>
                            {isEditable &&
                              formik.values.questions.length > 1 && (
                                <IconButton
                                  sx={{ color: color.textColor }}
                                  onClick={() => remove(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                          </Box>

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.question`}
                            placeholder="Enter Question"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            error={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    question: string;
                                  }
                                ).question,
                              )
                            }
                            helperText={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              (
                                formik.errors.questions[index] as {
                                  question: string;
                                }
                              ).question
                            }
                          />

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.correctAnswer`}
                            placeholder="Enter Correct Answer"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            multiline
                            rows={3}
                            inputProps={{ maxLength: 150 }}
                            error={
                              formik.touched.questions?.[index]
                                ?.correctAnswer &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    correctAnswer: string;
                                  }
                                ).correctAnswer,
                              )
                            }
                            helperText={`${
                              formik.values.questions[
                                index
                              ].correctAnswer?.split(" ").length || 0
                            }/150 words`}
                            sx={{ mt: 2 }}
                          />
                        </Box>
                      ))}

                    {/* ── MCQ ── */}
                    {questionType === "MCQ" &&
                      formik.values.questions.map((question, index) => (
                        <Box
                          key={index}
                          sx={{
                            borderRadius: "8px",
                            padding: "16px",
                            paddingBottom: 0,
                            position: "relative",
                            mb: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              mb: 2,
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: color.textColor,
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {`Question : ${index + 1}`}
                            </Typography>
                            {isEditable &&
                              formik.values.questions.length > 1 && (
                                <IconButton
                                  sx={{ color: color.textColor }}
                                  onClick={() => remove(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                          </Box>

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.question`}
                            placeholder="Enter MCQ Question"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            error={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    question: string;
                                  }
                                ).question,
                              )
                            }
                            helperText={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              (
                                formik.errors.questions[index] as {
                                  question: string;
                                }
                              ).question
                            }
                          />

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              justifyContent: "space-between",
                              gap: "16px",
                              mt: 1,
                            }}
                          >
                            {formik.values.questions[index].options.map(
                              (_, optionIndex) => (
                                <Box
                                  key={optionIndex}
                                  sx={{
                                    mt: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    width: isSmallScreen ? "100%" : "40%",
                                  }}
                                >
                                  <span
                                    style={{
                                      marginRight: 8,
                                      background: color.firstColor,
                                      borderRadius: "50%",
                                      width: "40px",
                                      height: "40px",
                                      textAlign: "center",
                                      paddingTop: "8.5px",
                                      color: "white",
                                    }}
                                  >
                                    {String.fromCharCode(65 + optionIndex)}
                                  </span>
                                  <Field
                                    className="input-root2"
                                    as={TextField}
                                    name={`questions.${index}.options.${optionIndex}`}
                                    placeholder={`Option ${optionIndex + 1}`}
                                    disabled={!isEditable}
                                    onBlur={formik.handleBlur}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      formik.handleChange(e);
                                    }}
                                    sx={{ height: "50px", width: "100%" }}
                                  />
                                </Box>
                              ),
                            )}
                          </Box>

                          <FormControl
                            className="input-root2"
                            sx={{ mt: 4, width: "100%" }}
                          >
                            <Select
                              value={
                                formik.values.questions[index].correctOption
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `questions.${index}.correctOption`,
                                  e.target.value,
                                );
                              }}
                              disabled={!isEditable}
                              displayEmpty
                              style={{
                                background: color.firstColor,
                                color: "white",
                              }}
                              IconComponent={(props) => (
                                <ArrowDropDown
                                  {...props}
                                  style={{ color: "white" }}
                                />
                              )}
                            >
                              <MenuItem value="" disabled>
                                Select Correct Option
                              </MenuItem>
                              {formik.values.questions[index].options.map(
                                (_, optionIndex) => (
                                  <MenuItem
                                    value={optionIndex}
                                    key={optionIndex}
                                  >
                                    Option{" "}
                                    {String.fromCharCode(65 + optionIndex)}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          </FormControl>
                        </Box>
                      ))}

                    {/* ── YES/NO ── */}
                    {questionType === "Yes/No" &&
                      formik.values.questions.map((question, index) => (
                        <Box
                          key={index}
                          sx={{
                            borderRadius: "8px",
                            padding: "16px",
                            paddingBottom: 0,
                            position: "relative",
                            mb: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              mb: 2,
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: color.textColor,
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {`Question : ${index + 1}`}
                            </Typography>
                            {isEditable &&
                              formik.values.questions.length > 1 && (
                                <IconButton
                                  sx={{ color: color.textColor }}
                                  onClick={() => remove(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                          </Box>

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.question`}
                            placeholder="Enter Yes/No Question"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            error={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    question: string;
                                  }
                                ).question,
                              )
                            }
                            helperText={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              (
                                formik.errors.questions[index] as {
                                  question: string;
                                }
                              ).question
                            }
                          />

                          <FormControl
                            className="input-root2"
                            fullWidth
                            sx={{
                              mt: 2,
                              width: isSmallScreen ? "100%" : "30%",
                            }}
                          >
                            <Select
                              value={
                                formik.values.questions[index].correctOption
                              }
                              onChange={(e) => {
                                formik.setFieldValue(
                                  `questions.${index}.correctOption`,
                                  e.target.value,
                                );
                              }}
                              disabled={!isEditable}
                              displayEmpty
                              style={{
                                background: color.firstColor,
                                color: "white",
                              }}
                              IconComponent={(props) => (
                                <ArrowDropDown
                                  {...props}
                                  style={{ color: "white" }}
                                />
                              )}
                            >
                              <MenuItem value="" disabled>
                                Select Correct Option
                              </MenuItem>
                              <MenuItem value="yes">Yes</MenuItem>
                              <MenuItem value="no">No</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      ))}

                    {/* ── FILL IN THE BLANKS ── */}
                    {questionType === "Fill in the blanks" &&
                      formik.values.questions.map((question, index) => (
                        <Box
                          key={index}
                          sx={{
                            borderRadius: "8px",
                            padding: "16px",
                            paddingBottom: 0,
                            position: "relative",
                            mb: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              mb: 2,
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color: color.textColor,
                                fontSize: "20px",
                                fontWeight: "bold",
                              }}
                            >
                              {`Question : ${index + 1}`}
                            </Typography>
                            {isEditable &&
                              formik.values.questions.length > 1 && (
                                <IconButton
                                  sx={{ color: color.textColor }}
                                  onClick={() => remove(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                          </Box>

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.question`}
                            placeholder="Enter Fill-in-the-blanks Question"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            error={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    question: string;
                                  }
                                ).question,
                              )
                            }
                            helperText={
                              formik.touched.questions?.[index]?.question &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              (
                                formik.errors.questions[index] as {
                                  question: string;
                                }
                              ).question
                            }
                          />

                          <Field
                            className="input-root2"
                            as={TextField}
                            name={`questions.${index}.correctAnswer`}
                            placeholder="Enter Correct Answer"
                            fullWidth
                            disabled={!isEditable}
                            onBlur={formik.handleBlur}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              formik.handleChange(e);
                            }}
                            inputProps={{ minLength: 10, maxLength: 20 }}
                            error={
                              formik.touched.questions?.[index]
                                ?.correctAnswer &&
                              formik.errors.questions &&
                              typeof formik.errors.questions[index] ===
                                "object" &&
                              Boolean(
                                (
                                  formik.errors.questions[index] as {
                                    correctAnswer: string;
                                  }
                                ).correctAnswer,
                              )
                            }
                            helperText={`${
                              formik.values.questions[index].correctAnswer
                                ?.length || 0
                            }/20 characters`}
                            sx={{ mt: 2 }}
                          />
                        </Box>
                      ))}

                    <Box
                      mt={12}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 2,
                      }}
                    >
                      <Button
                        id="button"
                        sx={{ marginLeft: "16px" }}
                        type="button"
                        onClick={() => handlePreview(formik)}
                      >
                        Next
                      </Button>

                      {isEditable && (
                        <Button
                          id="button1"
                          onClick={() => {
                            const lastQ =
                              formik.values.questions[
                                formik.values.questions.length - 1
                              ];
                            if (lastQ.question !== "") {
                              push({
                                question: "",
                                options: ["", "", "", ""],
                                correctOption: "",
                                correctAnswer: "",
                              });
                            } else {
                              alert(
                                "Please fill the previous section(s) before adding a new question.",
                              );
                            }
                          }}
                        >
                          + Add&nbsp;Question
                        </Button>
                      )}
                    </Box>
                  </div>
                )}
              </FieldArray>
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default QuestionForm;
